/** Phase 9 Slice 1 — Template Picker Modal (3-step wizard).
 *
 *  Step 1: Pick template — grid of built-in real-estate archetypes for the
 *          tenant. Click → step 2.
 *  Step 2: Customize — name / budget / regions overrides. Debounced (350ms)
 *          POST /campaign-templates/{id}/preview shows live preview pane.
 *  Step 3: Confirm — DSGVO/Impressum compliance notes; required checkbox
 *          gates the "Erstellen" button; POST /instantiate; navigate to the
 *          new campaign with a success toast.
 *
 *  Mounted as a controlled <Dialog/> from the Campaigns list page (no
 *  standalone route; the modal replaces the Slice 9.1.1 stub page).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useLocale } from "../../lib/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

// ---- i18n (kept local for one file; mirrors keys in lib/i18n.js if added) ----
const COPY = {
  de: {
    title: "Aus Vorlage erstellen",
    subtitle: "Vorgefertigte Kampagnen-Archetypen — in 3 Schritten zum Entwurf.",
    step_label: "Schritt {n} von 3",
    step1: { title: "Vorlage wählen", empty: "Keine Vorlagen verfügbar.",
              forbidden: "Keine Berechtigung." },
    step2: {
      title: "Anpassen",
      back: "Zurück", next: "Weiter",
      name: "Kampagnenname", name_ph: "Optional — leer = Vorlagenname",
      budget: "Tagesbudget (€)",
      regions: "Regionen",
      preview_h: "Live-Vorschau",
      preview_budget: "Budget: €{x}/Tag",
      preview_runtime: "Laufzeit: {from} – {to}",
      preview_brief: "Briefing",
      previewing: "Aktualisiere…",
    },
    step3: {
      title: "Bestätigen",
      compliance_h: "Compliance-Hinweise",
      ack: "Ich habe die DSGVO-/Impressum-Hinweise gelesen.",
      back: "Zurück",
      confirm: "Erstellen",
      creating: "Erstelle…",
      err_403: "Nur Manager dürfen Kampagnen erstellen.",
      err_generic: "Erstellung fehlgeschlagen.",
      ok: "Kampagne erstellt: {a}",
    },
  },
  en: {
    title: "Create from template",
    subtitle: "Pre-built campaign archetypes — 3 steps to a draft.",
    step_label: "Step {n} of 3",
    step1: { title: "Pick a template", empty: "No templates available.",
              forbidden: "Not authorised." },
    step2: {
      title: "Customize",
      back: "Back", next: "Next",
      name: "Campaign name", name_ph: "Optional — leave blank to use template name",
      budget: "Daily budget (€)",
      regions: "Regions",
      preview_h: "Live preview",
      preview_budget: "Budget: €{x}/day",
      preview_runtime: "Runtime: {from} – {to}",
      preview_brief: "Brief",
      previewing: "Refreshing…",
    },
    step3: {
      title: "Confirm",
      compliance_h: "Compliance notes",
      ack: "I have read the GDPR / imprint reminders.",
      back: "Back",
      confirm: "Create",
      creating: "Creating…",
      err_403: "Only managers may create campaigns.",
      err_generic: "Creation failed.",
      ok: "Campaign created: {a}",
    },
  },
};
const tt = (locale, path, vars) => {
  const parts = path.split(".");
  let cur = COPY[locale] || COPY.de;
  for (const p of parts) { cur = cur && cur[p]; }
  if (typeof cur !== "string") return path;
  return vars
    ? cur.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`))
    : cur;
};

function loc(obj, locale) {
  if (!obj) return "";
  return obj[locale] || obj.de || obj.en || "";
}

function fmtDay(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch (_) { return "—"; }
}

// ---- The Modal ----

export function TemplatePickerDialog({ open, onOpenChange }) {
  const { locale } = useLocale();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [rows, setRows] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [selected, setSelected] = useState(null);
  // Step 2 overrides
  const [nameOverride, setNameOverride] = useState("");
  const [budgetOverride, setBudgetOverride] = useState("");
  const [regionsOverride, setRegionsOverride] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const previewTimer = useRef(null);
  // Step 3
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load templates when opened.
  useEffect(() => {
    if (!open) return;
    setStep(1); setSelected(null); setLoadErr(null);
    setNameOverride(""); setBudgetOverride(""); setRegionsOverride([]);
    setAck(false); setPreview(null);
    api.get("/campaign-templates")
      .then((r) => setRows(r.data.templates || []))
      .catch((e) => setLoadErr(e?.response?.status === 403
                                  ? "forbidden" : "failed"));
  }, [open]);

  const overrides = useMemo(() => {
    const o = {};
    if (nameOverride.trim()) o.name_override = nameOverride.trim();
    if (budgetOverride !== "" && !isNaN(Number(budgetOverride))) {
      o.budget_eur_per_day_override = Number(budgetOverride);
    }
    if (regionsOverride.length) o.regions_override = regionsOverride;
    return o;
  }, [nameOverride, budgetOverride, regionsOverride]);

  // Debounced preview fetch (350 ms).
  useEffect(() => {
    if (step !== 2 || !selected) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    setPreviewing(true);
    previewTimer.current = setTimeout(async () => {
      try {
        const r = await api.post(
          `/campaign-templates/${selected.id}/preview`, overrides);
        setPreview(r.data.preview || null);
      } catch (_) {
        setPreview(null);
      } finally { setPreviewing(false); }
    }, 350);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [step, selected, overrides]);

  function pickTemplate(t) {
    setSelected(t);
    // Pre-fill defaults so live preview is sensible.
    setNameOverride("");
    setBudgetOverride(String(t.default_budget_eur_per_day || ""));
    setRegionsOverride(t.default_regions || []);
    setStep(2);
  }

  function toggleRegion(r) {
    setRegionsOverride((cur) =>
      cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]);
  }

  async function confirmCreate() {
    if (!selected || !ack) return;
    setSubmitting(true);
    try {
      const r = await api.post(
        `/campaign-templates/${selected.id}/instantiate`, overrides);
      const cid = r.data.campaign_id;
      toast.success(tt(locale, "step3.ok", { a: selected.archetype_key }));
      onOpenChange(false);
      nav(`/app/campaigns/${cid}`);
    } catch (e) {
      if (e?.response?.status === 403)
        toast.error(tt(locale, "step3.err_403"));
      else toast.error(tt(locale, "step3.err_generic"));
    } finally { setSubmitting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="templatepicker-dialog"
        className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tt(locale, "title")}</DialogTitle>
          <DialogDescription className="text-xs">
            {tt(locale, "subtitle")} ·{" "}
            <span data-testid="tpl-step-indicator" className="font-mono">
              {tt(locale, "step_label", { n: step })}
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* ---------- Step 1 ---------- */}
        {step === 1 && (
          <div data-testid="tpl-step-1" className="space-y-3">
            <h3 className="text-sm font-medium">{tt(locale, "step1.title")}</h3>
            {loadErr === "forbidden" && (
              <p data-testid="tpl-forbidden"
                  className="text-sm text-rose-600">
                {tt(locale, "step1.forbidden")}
              </p>
            )}
            {loadErr !== "forbidden" && rows.length === 0 && (
              <p data-testid="tpl-empty"
                  className="text-sm text-muted-foreground">
                {tt(locale, "step1.empty")}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rows.map((t) => (
                <Card
                  key={t.id}
                  data-testid={`tpl-card-${t.archetype_key}`}
                  className="cursor-pointer transition-shadow hover:shadow-md"
                  onClick={() => pickTemplate(t)}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-sm">
                        {loc(t.display_name, locale)}
                      </CardTitle>
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {t.default_campaign_kind}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-xs">
                    <p className="text-muted-foreground">
                      {loc(t.description, locale)}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {(t.default_platforms || []).map((p) => (
                        <Badge key={p} variant="secondary"
                                className="font-mono text-[10px]">{p}</Badge>
                      ))}
                      <Badge variant="outline"
                              className="font-mono text-[10px]">
                        €{t.default_budget_eur_per_day}/Tag
                      </Badge>
                      <Badge variant="outline"
                              className="font-mono text-[10px]">
                        {t.default_runtime_days} Tage
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ---------- Step 2 ---------- */}
        {step === 2 && selected && (
          <div data-testid="tpl-step-2"
                className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="text-sm font-medium">
                {tt(locale, "step2.title")} — {loc(selected.display_name, locale)}
              </h3>
              <div className="space-y-1">
                <Label htmlFor="tpl-name-input" className="text-xs">
                  {tt(locale, "step2.name")}
                </Label>
                <Input id="tpl-name-input" data-testid="tpl-name-input"
                        value={nameOverride}
                        placeholder={tt(locale, "step2.name_ph")}
                        onChange={(e) => setNameOverride(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="tpl-budget-input" className="text-xs">
                  {tt(locale, "step2.budget")}
                </Label>
                <Input id="tpl-budget-input" data-testid="tpl-budget-input"
                        type="number" min={0}
                        value={budgetOverride}
                        onChange={(e) => setBudgetOverride(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{tt(locale, "step2.regions")}</Label>
                <div className="flex flex-wrap gap-1"
                      data-testid="tpl-regions-chips">
                  {(selected.default_regions || []).map((r) => {
                    const on = regionsOverride.includes(r);
                    return (
                      <button
                        type="button" key={r}
                        data-testid={`tpl-region-${r.replace(/\s+/g,"_")}`}
                        onClick={() => toggleRegion(r)}
                        className={[
                          "text-[11px] px-2 py-1 rounded-full border",
                          on
                            ? "bg-foreground text-background border-foreground"
                            : "bg-transparent text-muted-foreground hover:bg-accent",
                        ].join(" ")}>
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-2 rounded-md border bg-muted/30 p-3"
                  data-testid="tpl-preview-pane">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium uppercase tracking-wide
                                  text-muted-foreground">
                  {tt(locale, "step2.preview_h")}
                </h4>
                {previewing && (
                  <Loader2 data-testid="tpl-preview-spinner"
                            className="size-3 animate-spin text-muted-foreground" />
                )}
              </div>
              {preview && (
                <div className="space-y-1.5 text-xs">
                  <div data-testid="tpl-preview-name" className="font-medium">
                    {preview.name}
                  </div>
                  <div data-testid="tpl-preview-budget"
                        className="text-muted-foreground">
                    {tt(locale, "step2.preview_budget",
                         { x: preview.budget?.amount })}
                  </div>
                  <div data-testid="tpl-preview-runtime"
                        className="text-muted-foreground">
                    {tt(locale, "step2.preview_runtime", {
                      from: fmtDay(preview.schedule?.start_at),
                      to: fmtDay(preview.schedule?.end_at),
                    })}
                  </div>
                  <div data-testid="tpl-preview-regions"
                        className="flex flex-wrap gap-1 pt-1">
                    {(preview.regions || []).map((r) =>
                      <Badge key={r} variant="outline"
                              className="font-mono text-[10px]">{r}</Badge>)}
                  </div>
                  <div className="pt-2">
                    <div className="text-[10px] uppercase tracking-wide
                                       text-muted-foreground">
                      {tt(locale, "step2.preview_brief")}
                    </div>
                    <p data-testid="tpl-preview-brief"
                        className="text-muted-foreground line-clamp-3">
                      {loc(preview.brief, locale)}
                    </p>
                  </div>
                </div>
              )}
              {!preview && !previewing && (
                <p className="text-xs text-muted-foreground">…</p>
              )}
            </div>
          </div>
        )}

        {/* ---------- Step 3 ---------- */}
        {step === 3 && selected && (
          <div data-testid="tpl-step-3" className="space-y-4">
            <h3 className="text-sm font-medium">
              {tt(locale, "step3.title")} — {loc(selected.display_name, locale)}
            </h3>
            {preview && (
              <Card>
                <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-2
                                                gap-2 text-xs">
                  <div><b>Name:</b> {preview.name}</div>
                  <div><b>Type:</b> {preview.campaign_type}</div>
                  <div><b>Budget:</b> €{preview.budget?.amount}/Tag</div>
                  <div><b>KPI:</b> {preview.goal_kpi}</div>
                  <div className="md:col-span-2">
                    <b>Regionen:</b> {(preview.regions || []).join(", ")}
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="rounded-md border border-amber-300 bg-amber-50
                              dark:bg-amber-950/30 dark:border-amber-800 p-3">
              <div className="text-xs font-medium text-amber-900
                                dark:text-amber-200 uppercase tracking-wide">
                {tt(locale, "step3.compliance_h")}
              </div>
              <p data-testid="tpl-compliance-text"
                  className="text-xs text-amber-900 dark:text-amber-100 mt-1
                              whitespace-pre-line">
                {loc(selected.compliance_notes, locale)}
              </p>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox
                data-testid="tpl-compliance-checkbox"
                checked={ack}
                onCheckedChange={(v) => setAck(v === true)} />
              <span>{tt(locale, "step3.ack")}</span>
            </label>
          </div>
        )}

        {/* ---------- Footer ---------- */}
        <DialogFooter className="flex items-center justify-between gap-2">
          {step === 1 && <div />}
          {step === 2 && (
            <>
              <Button variant="ghost" size="sm"
                      data-testid="tpl-back-2"
                      onClick={() => setStep(1)}>
                <ArrowLeft className="size-3 mr-1" />
                {tt(locale, "step2.back")}
              </Button>
              <Button size="sm" data-testid="tpl-next-2"
                      onClick={() => setStep(3)}
                      disabled={!preview}>
                {tt(locale, "step2.next")}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="ghost" size="sm"
                      data-testid="tpl-back-3"
                      onClick={() => setStep(2)}>
                <ArrowLeft className="size-3 mr-1" />
                {tt(locale, "step3.back")}
              </Button>
              <Button size="sm" data-testid="tpl-confirm-btn"
                      disabled={!ack || submitting}
                      onClick={confirmCreate}>
                {submitting
                  ? tt(locale, "step3.creating")
                  : tt(locale, "step3.confirm")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Default export retained for backward-compat with the prior route + test
// import; the modal can also be opened standalone for storybook/manual QA.
export default function CampaignsFromTemplate() {
  const [open, setOpen] = useState(true);
  return (
    <div className="p-6" data-testid="from-template-page">
      <Button data-testid="from-template-open"
              onClick={() => setOpen(true)}>Open</Button>
      <TemplatePickerDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
